import { useLanguage } from "../i18n/LanguageContext";
import { SEOHead } from "../components/SEOHead";
import { PageHeader } from "../components/PageHeader";
import { siteConfig, isPlaceholder } from "../config/site";

const Value = ({ v, testId }: { v: string; testId: string }) => {
  const { t } = useLanguage();
  return isPlaceholder(v) ? (
    <span className="inline-flex items-center gap-2 rounded-md bg-clay-50 px-2 py-0.5 font-mono text-sm text-clay" title={t.legal.placeholderNote} data-testid={testId}>{v}</span>
  ) : <span data-testid={testId}>{v}</span>;
};

export default function Impressum() {
  const { t } = useLanguage();
  const l = siteConfig.legal;
  const s = t.legal.sections;

  const rows: [string, React.ReactNode][] = [
    [s.provider, <Value v={l.officialBusinessName} testId="legal-business-name" />],
    [s.represented, <Value v={l.ownerFullName} testId="legal-owner-name" />],
    [t.common.address, <Value v={l.businessAddress} testId="legal-address" />],
    [
      s.contact,
      <div className="grid gap-3" data-testid="legal-contact">
        {siteConfig.contacts.map((c, i) => (
          <div key={i} className="grid gap-0.5">
            <span className="text-sm font-medium text-charcoal-500">{c.name}</span>
            <a href={`tel:${c.phoneHref}`} className="hover:text-clay transition-colors" data-testid={`legal-phone-${i}`}>{c.phone}</a>
            <a href={`mailto:${c.email}`} className="break-all hover:text-clay transition-colors" data-testid={`legal-email-${i}`}>{c.email}</a>
          </div>
        ))}
      </div>,
    ],
    [s.tax, <Value v={l.taxInformation} testId="legal-tax" />],
    [
      s.responsible,
      <span data-testid="legal-responsible">{siteConfig.contacts[0].name}, {l.businessAddress}</span>,
    ],
  ];

  const disclaimers: [string, string, string][] = [
    [s.liabilityContent, s.liabilityContentText, "legal-liability-content"],
    [s.liabilityLinks, s.liabilityLinksText, "legal-liability-links"],
    [s.copyright, s.copyrightText, "legal-copyright"],
    [s.dispute, s.disputeText, "legal-dispute"],
  ];

  return (
    <main data-testid="impressum-page">
      <SEOHead title={t.meta.impressum.title} description={t.meta.impressum.description} />
      <PageHeader eyebrow={t.nav.impressum} title={t.legal.impressumTitle} text={t.legal.impressumIntro} crumb={t.nav.impressum} testId="impressum-header" />
      <section className="container-x max-w-3xl pb-24 md:pb-32">
        <dl className="divide-y divide-sand-200 rounded-3xl border border-sand-200 bg-white">
          {rows.map(([label, value], i) => (
            <div key={i} className="grid gap-1 px-6 py-5 sm:grid-cols-3">
              <dt className="text-sm font-medium text-charcoal-500">{label}</dt>
              <dd className="sm:col-span-2 leading-relaxed">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-14 grid gap-10">
          {disclaimers.map(([title, body, testId]) => (
            <div key={testId} data-testid={testId}>
              <h2 className="font-display text-xl font-medium">{title}</h2>
              <p className="mt-2 text-charcoal-700 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
